import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatDate, formatEUR, label } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CustomerDialog } from "@/components/CustomerDialog";
import { DeleteCustomerDialog } from "@/components/DeleteCustomerDialog";
import { Download, UploadCloud, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const custName = (c) => c.dati_fiscali?.ragione_sociale || `${c.anagrafica?.nome || ""} ${c.anagrafica?.cognome || ""}`.trim();

export default function AdminCustomers() {
  const api = useApi();
  const qc = useQueryClient();
  const fileRefs = useRef({});
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, customer: null });
  const [toDelete, setToDelete] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["admin-customers"], queryFn: async () => (await api.get("/admin/customers")).data });
  const { data: resellers = [] } = useQuery({ queryKey: ["admin-resellers"], queryFn: async () => (await api.get("/admin/resellers")).data });
  const { data: products = [] } = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await api.get("/admin/products")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-customers"] }); qc.invalidateQueries({ queryKey: ["admin-approvals"] }); };
  const err = (e) => toast.error(e.response?.data?.detail || "Errore");

  const save = useMutation({
    mutationFn: async (f) => {
      const c = dialog.customer;
      if (!c) {
        const { trial_end, license_end, status, ...body } = f;
        return { created: (await api.post("/admin/customers", body)).data };
      }
      if (f.reseller_id !== c.reseller_id) await api.patch(`/admin/customers/${c.id}/reseller`, { reseller_id: f.reseller_id });
      await api.put(`/admin/customers/${c.id}`, { anagrafica: f.anagrafica, dati_fiscali: f.dati_fiscali, module_ids: f.module_ids, status: f.status, trial_end: f.trial_end || null, license_end: f.license_end || null });
      return {};
    },
    onSuccess: (r) => { setDialog({ open: false, customer: null }); refresh(); toast.success(r.created ? "Cliente creato \u2014 modulo d'ordine generato" : "Cliente aggiornato"); if (r.created?.order_pdf_id) openPdf(r.created.order_pdf_id); },
    onError: err,
  });
  const upload = useMutation({
    mutationFn: async ({ id, file }) => { const fd = new FormData(); fd.append("file", file); return (await api.post(`/admin/customers/${id}/signed-order`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data; },
    onSuccess: () => { refresh(); toast.success("Modulo firmato caricato: lo approvi in Approvazioni"); },
    onError: err,
  });
  const remove = useMutation({
    mutationFn: async (ids) => (await api.post(`/admin/customers/${toDelete.id}/delete`, { delete_commission_ids: ids })).data,
    onSuccess: (r) => { setToDelete(null); refresh(); qc.invalidateQueries({ queryKey: ["admin-commissions"] }); toast.success(`Cliente cancellato${r.commissions_deleted ? ` con ${r.commissions_deleted} ${r.commissions_deleted === 1 ? "provvigione" : "provvigioni"}` : ""}`); },
    onError: err,
  });
  const openPdf = async (id) => { const r = await api.get(`/files/${id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); };

  const s = q.toLowerCase();
  const rows = data.filter((c) => !s || [custName(c), c.anagrafica?.email, c.reseller_name, c.reseller_code, c.product_name].some((v) => (v || "").toLowerCase().includes(s)));

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Clienti</h1>
          <p className="text-sm text-slate-500 mt-1">Tutti i clienti dei rivenditori: inserisca, modifichi o cambi il rivenditore associato.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o rivenditore" className="w-60" data-testid="admin-customers-search" />
          <Button onClick={() => setDialog({ open: true, customer: null })} className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="btn-admin-new-customer"><Plus size={14} className="mr-1" /> Nuovo cliente</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#E2DDD5] bg-white">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
            <tr><th className="text-left px-3 py-3">Cliente</th><th className="text-left px-3 py-3">Rivenditore</th><th className="text-left px-3 py-3">Prodotto</th><th className="text-right px-3 py-3">Canone</th><th className="text-left px-3 py-3">Stato</th><th className="text-left px-3 py-3">Scadenza</th><th /></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Caricamento…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Nessun cliente.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-[#E2DDD5]" data-testid={`admin-cust-${c.id}`}>
                <td className="px-3 py-3"><div className="font-medium">{custName(c)}</div><div className="text-xs text-slate-500">{c.anagrafica?.email}</div></td>
                <td className="px-3 py-3"><div>{c.reseller_name || "\u2014"}</div><div className="text-xs font-mono text-slate-500">{c.reseller_code}</div></td>
                <td className="px-3 py-3">{c.product_name || "\u2014"}</td>
                <td className="px-3 py-3 text-right font-mono">{c.license_price != null ? formatEUR(c.license_price) : "\u2014"}</td>
                <td className="px-3 py-3">{label(c.status)}</td>
                <td className="px-3 py-3 text-slate-500">{formatDate(c.license_end)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {c.order_pdf_id && <Button variant="ghost" size="sm" onClick={() => openPdf(c.order_pdf_id)} data-testid={`admin-cust-pdf-${c.id}`}><Download size={14} className="mr-1" /> Modulo</Button>}
                  {c.status === "pending_approval" && !c.signed_order_pdf_id && (
                    <>
                      <input type="file" accept="application/pdf,image/*" className="hidden" ref={(el) => (fileRefs.current[c.id] = el)} onChange={(e) => e.target.files?.[0] && upload.mutate({ id: c.id, file: e.target.files[0] })} />
                      <Button variant="outline" size="sm" className="ml-1" onClick={() => fileRefs.current[c.id]?.click()} data-testid={`admin-cust-upload-${c.id}`}><UploadCloud size={14} className="mr-1" /> Carica firmato</Button>
                    </>
                  )}
                  <Button variant="outline" size="sm" className="ml-1" onClick={() => setDialog({ open: true, customer: c })} data-testid={`admin-cust-edit-${c.id}`}><Pencil size={14} className="mr-1" /> Modifica</Button>
                  <Button variant="ghost" size="sm" className="ml-1" onClick={() => setToDelete(c)} title="Cancella cliente" data-testid={`admin-cust-delete-${c.id}`}><Trash2 size={14} className="text-rose-700" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CustomerDialog api={api} open={dialog.open} onOpenChange={(v) => setDialog({ open: v, customer: v ? dialog.customer : null })} customer={dialog.customer}
                      resellers={resellers} products={products} onSave={(f) => save.mutate(f)} saving={save.isPending} />
      <DeleteCustomerDialog api={api} customer={toDelete} name={toDelete ? custName(toDelete) : ""} onOpenChange={(v) => !v && setToDelete(null)}
                            onConfirm={(ids) => remove.mutate(ids)} deleting={remove.isPending} />
    </div>
  );
}
